// Modelslistview.jsx
//
// Lists forecast models. + New Model opens the model form modal; each
// row has Edit (model + personas) and Duplicate (atomic copy) actions.
// Delete is intentionally NOT exposed here - the delete-model affordance
// lives inside the edit modal (set Active to false) to prevent accidental
// loss of run history. If a hard "destroy" is ever needed it'll be a
// separate confirm-typing-the-name flow.

import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Snackbar, Typography,
} from "@mui/material";

import { getForecastModels } from "../../services/forecast/forecastservices";
import ModelFormModal      from "./modals/ModelFormModal";
import ModelDuplicateModal from "./modals/ModelDuplicateModal";
import ChannelDayMixModal  from "./modals/ChannelDayMixModal";

const BLUE  = "#378ADD";
const GREEN = "#1D9E75";
const AMBER = "#BA7517";

// ── Helpers ────────────────────────────────────────────────────────
function safeNum(v) { const n = Number(v); return isNaN(n) ? 0 : n; }

function fmtCurrency(n) {
  const num = safeNum(n);
  if (!num) return "$0";
  if (Math.abs(num) >= 1_000_000) return `$${(num/1_000_000).toFixed(1)}M`;
  if (Math.abs(num) >= 1_000)     return `$${(num/1_000).toFixed(1)}K`;
  return `$${num.toFixed(0)}`;
}

function fmtDate(val) {
  if (!val) return "—";
  const d = new Date(val);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

// ── Layout ─────────────────────────────────────────────────────────
//
// Run history columns (Runs, Last Run) were removed - those properly
// belong on the Forecast tab where you actually launch and inspect
// runs. This list is for model definition and shape, so we keep:
//
//   Model         - the name
//   Description   - free-text intent
//   Channels      - count of active channels in the model
//   Latest $      - last produced forecast total, for at-a-glance
//                   "is this model alive" signal
//   Status        - active / inactive pill
//   Actions       - Channel Mix / Edit / Duplicate buttons
//
// The Actions column is widened so the three buttons sit comfortably
// on one line without colliding with the Status pill on the left.
const COLS = [
  { key: "name",           label: "Model",        align: "left",  width: "1.4fr" },
  { key: "description",    label: "Description",  align: "left",  width: "2.6fr" },
  { key: "persona_count",  label: "Channels",     align: "right", width: "0.7fr", type: "int"  },
  { key: "latest_total_predicted_collections",
                           label: "Latest $",     align: "right", width: "0.9fr", type: "cur"  },
  { key: "is_active",      label: "Status",       align: "left",  width: "0.7fr", type: "status" },
  { key: "actions",        label: "",             align: "right", width: "2.4fr", type: "actions" },
];
const GRID = COLS.map(c => c.width).join(" ");


// ── Inline primitives ──────────────────────────────────────────────
function ActionButton({ children, onClick, color = BLUE, disabled }) {
  return (
    <Box
      onClick={disabled ? undefined : onClick}
      sx={{
        px: "10px", py: "3px",
        fontSize: 11, borderRadius: "20px",
        cursor: disabled ? "not-allowed" : "pointer", userSelect: "none",
        border: "0.5px solid", borderColor: color,
        background: "transparent", color, fontWeight: 500,
        opacity: disabled ? 0.5 : 1,
        // whiteSpace: nowrap keeps labels like "Channel Mix" on one line
        // even when the row is narrow; the actions column has been sized
        // wide enough that this won't push other content off-screen.
        whiteSpace: "nowrap",
        transition: "all .15s",
        "&:hover": { background: disabled ? "transparent" : `${color}18` },
        display: "inline-block",
      }}
    >
      {children}
    </Box>
  );
}

function StatusPill({ active }) {
  const color = active ? GREEN : "#888";
  return (
    <Box component="span" sx={{
      display: "inline-block", px: "7px", py: "1px",
      borderRadius: "20px", fontSize: 11, fontWeight: 500,
      bgcolor: `${color}18`, color, border: `0.5px solid ${color}44`,
    }}>
      {active ? "Active" : "Inactive"}
    </Box>
  );
}


// ── Row renderer ───────────────────────────────────────────────────
function ModelRow({ row, index, onEdit, onDuplicate, onChannelMix }) {
  const renderCell = (col) => {
    if (col.type === "actions") {
      return (
        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
          <ActionButton onClick={() => onChannelMix(row)} color={GREEN}>Channel Mix</ActionButton>
          <ActionButton onClick={() => onEdit(row)}>Edit</ActionButton>
          <ActionButton onClick={() => onDuplicate(row)} color={AMBER}>Duplicate</ActionButton>
        </Box>
      );
    }
    if (col.type === "status") {
      return <StatusPill active={row.is_active} />;
    }
    if (col.type === "date") {
      return (
        <Box component="span" sx={{ color: row[col.key] ? "text.secondary" : "text.disabled", fontSize: 11 }}>
          {fmtDate(row[col.key])}
        </Box>
      );
    }
    if (col.type === "cur") {
      const v = safeNum(row[col.key]);
      return (
        <Box component="span" sx={{ color: v > 0 ? AMBER : "text.disabled", fontWeight: v > 0 ? 500 : 400 }}>
          {v > 0 ? fmtCurrency(v) : "—"}
        </Box>
      );
    }
    if (col.type === "int") {
      const v = safeNum(row[col.key]);
      return (
        <Box component="span" sx={{ color: v > 0 ? "text.primary" : "text.disabled" }}>
          {v > 0 ? v.toLocaleString() : "—"}
        </Box>
      );
    }
    if (col.key === "name") {
      return (
        <Box sx={{ fontWeight: 500, color: "text.primary" }}>
          {row[col.key]}
        </Box>
      );
    }
    if (col.key === "description") {
      const text = row[col.key] || "";
      return (
        <Box sx={{
          color: text ? "text.secondary" : "text.disabled",
          fontSize: 12,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {text || "—"}
        </Box>
      );
    }
    return <Box component="span">{row[col.key]}</Box>;
  };

  return (
    <Box sx={{
      display: "grid", gridTemplateColumns: GRID, px: 2,
      py: "10px", fontSize: 12,
      borderBottom: "0.5px solid", borderColor: "divider",
      background: index % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
      "&:hover": { background: "rgba(55,138,221,0.06)" },
    }}>
      {COLS.map(col => (
        <Box key={col.key} sx={{
          display: "flex", alignItems: "center", minWidth: 0,
          justifyContent: col.align === "right" ? "flex-end" : "flex-start",
        }}>
          {renderCell(col)}
        </Box>
      ))}
    </Box>
  );
}


// ── Main view ──────────────────────────────────────────────────────
export default function ModelsListView() {
  const [models,  setModels]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  // Modal state
  const [modelModal,     setModelModal]     = useState({ open: false, mode: "create", modelId: null });
  const [duplicateModal, setDuplicateModal] = useState({ open: false, source: null });
  const [channelMixModal, setChannelMixModal] = useState({ open: false, modelId: null, modelName: "" });
  const [toast,          setToast]          = useState({ open: false, message: "", severity: "success" });

  const loadModels = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const data = await getForecastModels();
      setModels(data || []);
    } catch (e) {
      setError("Failed to load models");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadModels(); }, [loadModels]);

  // ── Action handlers ────────────────────────────────────────────
  const handleCreate    = ()    => setModelModal({ open: true, mode: "create", modelId: null });
  const handleEdit      = (row) => setModelModal({ open: true, mode: "edit",   modelId: row.id });
  const handleDuplicate = (row) => setDuplicateModal({ open: true, source: row });
  const handleChannelMix = (row) => setChannelMixModal({ open: true, modelId: row.id, modelName: row.name });

  const handleSaved = (msg) => {
    setToast({ open: true, message: msg, severity: "success" });
    loadModels();
  };

  return (
    <Box sx={{ p: 2 }}>

      {/* ── Header with action button ── */}
      <Box sx={{
        display: "flex", alignItems: "center", gap: 2,
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <Typography sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}>
          Forecast Models
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={handleCreate}
          variant="outlined"
          sx={{
            px: "14px", py: "5px", fontSize: 13, borderRadius: "20px",
            border: "0.5px solid", borderColor: BLUE, color: BLUE,
            fontWeight: 500, textTransform: "none",
            "&:hover": { background: "rgba(55,138,221,0.08)", borderColor: BLUE },
          }}
        >
          + New Model
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 2 }}>
          <CircularProgress size={20} /> Loading models…
        </Box>
      )}

      {!loading && (
        <>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1, mt: 2 }}>
            <Typography sx={{
              fontSize: 11, fontWeight: 500, color: "text.secondary",
              textTransform: "uppercase", letterSpacing: "0.06em",
            }}>
              All models
            </Typography>
            <Typography sx={{ fontSize: 11, color: "text.disabled" }}>
              ({models.length})
            </Typography>
          </Box>

          <Box sx={{
            border: "0.5px solid", borderColor: "divider",
            borderRadius: 2, overflow: "hidden",
          }}>
            <Box sx={{
              display: "grid", gridTemplateColumns: GRID, px: 2, py: 1,
              background: "#f7f7f7",
              borderBottom: "0.5px solid", borderColor: "divider",
              fontSize: 11, fontWeight: 500,
              textTransform: "uppercase", letterSpacing: "0.05em",
            }}>
              {COLS.map(col => (
                <Box key={col.key} sx={{ textAlign: col.align, color: "text.secondary" }}>
                  {col.label}
                </Box>
              ))}
            </Box>

            {models.length === 0 ? (
              <Box sx={{ p: 4, textAlign: "center", color: "text.disabled", fontSize: 12 }}>
                No models yet. Click "+ New Model" to create one.
              </Box>
            ) : (
              models.map((row, i) => (
                <ModelRow
                  key={row.id} row={row} index={i}
                  onEdit={handleEdit}
                  onDuplicate={handleDuplicate}
                  onChannelMix={handleChannelMix}
                />
              ))
            )}
          </Box>
        </>
      )}

      {/* ── Modals ── */}
      <ModelFormModal
        open={modelModal.open}
        mode={modelModal.mode}
        modelId={modelModal.modelId}
        onClose={() => setModelModal({ ...modelModal, open: false })}
        onSaved={() => handleSaved(modelModal.mode === "create" ? "Model created" : "Model updated")}
      />

      <ChannelDayMixModal
        open={channelMixModal.open}
        modelId={channelMixModal.modelId}
        modelName={channelMixModal.modelName}
        onClose={() => setChannelMixModal({ ...channelMixModal, open: false })}
        onSaved={() => handleSaved("Channel mix saved")}
      />

      <ModelDuplicateModal
        open={duplicateModal.open}
        sourceModel={duplicateModal.source}
        onClose={() => setDuplicateModal({ ...duplicateModal, open: false })}
        onSaved={() => handleSaved("Model duplicated")}
      />


      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast({ ...toast, open: false })}
          severity={toast.severity}
          sx={{ fontSize: 13 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}